import { useState } from "react";
import { Plus, Pencil, Trash2, Building2, Users, Globe, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ContactGroupDialog } from "./ContactGroupDialog";
import { useContactGroups } from "@/hooks/useContactGroups";
import { GROUP_TYPES, type ContactGroup } from "@/types/lead";

interface ContactGroupsManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

export function ContactGroupsManager({
  open,
  onOpenChange,
  workspaceId,
}: ContactGroupsManagerProps) {
  const { groups, createGroup, updateGroup, deleteGroup } = useContactGroups(workspaceId);
  const [searchTerm, setSearchTerm] = useState("");
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ContactGroup | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ContactGroup | null>(null);

  const filteredGroups = groups.filter(
    (group) =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.industry?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setIsGroupDialogOpen(true);
  };

  const handleEditGroup = (group: ContactGroup) => {
    setEditingGroup(group);
    setIsGroupDialogOpen(true);
  };

  const handleDeleteClick = (group: ContactGroup) => {
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;
    await deleteGroup(groupToDelete.id);
    setDeleteConfirmOpen(false);
    setGroupToDelete(null);
  };

  const handleSaveGroup = async (data: Partial<ContactGroup>) => {
    if (editingGroup) {
      return await updateGroup(editingGroup.id, data);
    }
    return await createGroup(data);
  };

  const getTypeLabel = (type?: string) => {
    return GROUP_TYPES.find((t) => t.value === type)?.label || type || "Empresa";
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Grupos de Contatos
            </DialogTitle>
            <DialogDescription>
              Organize seus leads em grupos como empresas, famílias ou parceiros.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Barra de busca e ação */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar grupos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={handleCreateGroup}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Grupo
              </Button>
            </div>

            {/* Lista de grupos */}
            <ScrollArea className="flex-1 -mx-6 px-6">
              {filteredGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    {searchTerm ? "Nenhum grupo encontrado" : "Nenhum grupo criado"}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {searchTerm
                      ? "Tente ajustar sua busca."
                      : "Crie grupos para organizar seus contatos por empresa, família ou outros critérios."}
                  </p>
                  {!searchTerm && (
                    <Button onClick={handleCreateGroup}>
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Primeiro Grupo
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredGroups.map((group) => (
                    <div
                      key={group.id}
                      className="flex items-start gap-3 p-4 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{group.name}</h4>
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {getTypeLabel(group.type)}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          {group.industry && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {group.industry}
                            </span>
                          )}
                          {group.website && (
                            <a
                              href={group.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 hover:text-primary"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe className="w-3 h-3" />
                              Website
                            </a>
                          )}
                          {group.size && (
                            <span className="capitalize">{group.size}</span>
                          )}
                        </div>

                        {group.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {group.description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditGroup(group)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteClick(group)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de criação/edição */}
      <ContactGroupDialog
        open={isGroupDialogOpen}
        onOpenChange={setIsGroupDialogOpen}
        group={editingGroup}
        onSave={handleSaveGroup}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o grupo "{groupToDelete?.name}"? Os leads vinculados não serão excluídos, apenas a associação será removida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
